import { useParams, useNavigate } from 'react-router-dom';
import { Avatar, Status } from '@shared/ui/parts';
import { ThemeButtonIcon } from '@shared/ui/buttons';
import EditTeamDisplay from '@widgets/teams/EditTeamDisplay';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  setGroupIdSearch,
  setGroupIdParams,
} from '@app/store/slices/groupIdSlice';
import TeamsIdTable from '@widgets/tables/TeamsIdTable';
import { useEffect, useCallback, useMemo } from 'react';
import { SearchTable } from '@shared/ui/forms/search';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@utils/libs/theme';
import { useMobileOverlay } from '@utils/hooks/useMobileOverlay';
import TableMenuBtn from '@widgets/tables/main/TableMenuBtn';
import InviteMemberBtn from '@widgets/teams/InviteMemberBtn';

export default function TeamsIdPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { user } = useSelector((state) => state.user);
  const { search, group, isNotFound } = useSelector((state) => state.group_id);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobileSearch = useMobileOverlay();

  useEffect(() => {
    dispatch(setGroupIdParams({ slug, page: 1 }));
  }, [slug]);

  useEffect(() => {
    if (isNotFound) navigate('/teams', { replace: true });
  }, [isNotFound]);

  const handleGroupIdSearchChange = useCallback(
    (nextSearch) => {
      dispatch(setGroupIdSearch(nextSearch));
    },
    [dispatch],
  );

  const searchElement = useMemo(() => {
    return (
      <SearchTable
        handleSearch={handleGroupIdSearchChange}
        value={search}
        placeholder={t('search_placeholder_my_users')}
      />
    );
  }, [handleGroupIdSearchChange, search, t]);

  return (
    <section className="teamId table-page main-section">
      {isMobile && mobileSearch.isOpen && (
        <div className="search-mobile-container">
          <div className="block-pd-x search-mobile">
            <ThemeButtonIcon icon="arrow-left" onClick={mobileSearch.close} />
            {searchElement}
          </div>
        </div>
      )}

      {!mobileSearch.isOpen && (
        <div className="block-pd-y block-pd-x page-heading">
          <div className="teamId__header">
            <ThemeButtonIcon
              icon="arrow-left"
              onClick={() => navigate('/teams')}
            />
            <div className="teamId__avatar">
              <Avatar team={group} />
            </div>
            <div className="teamId__header-group">
              <p
                className={`${isMobile ? 'title-h3m' : 'title-h2m'} teamId__header-name`}
              >
                {group?.name}
              </p>
              <p
                className={`tertiary-text ${isMobile ? 'body-14r' : 'title-h2m'}`}
              >
                {t('members')}
              </p>
              {group?.is_frozen && (
                <div>
                  {' '}
                  <Status data="frozen" />
                </div>
              )}
            </div>
          </div>
          <div className="btns-toolbar min-gap">
            {!user?.group_role && !isMobile && <EditTeamDisplay />}
            {!isMobile && <InviteMemberBtn />}
            {isMobile && (
              <>
                <ThemeButtonIcon icon="search" onClick={mobileSearch.open} />
                <TableMenuBtn menu="teamId" data={group} />
              </>
            )}
          </div>
        </div>
      )}

      <div className="block-pd-x table-page__box">
        {!isMobile && (
          <div className="table-filters">
            <div className="table-filters-row">{searchElement}</div>
          </div>
        )}
        <div className="table-page__table">
          <TeamsIdTable />
        </div>
      </div>
    </section>
  );
}
